import {
  Disclaimer,
  Div,
  H5,
  RestaurantReferral,
  lightTheme,
} from '@vgrubs/components';
import { formatReferralDay } from '../helpers/referralLabels';
import { Step, StepDot, StepList } from './ReferralTimeline.styled';

type TimelineStep = {
  title: string;
  /** The date, or what it is waiting on. */
  detail: string;
  done: boolean;
  color: string;
};

/**
 * The steps out of the four dates: signed up, verified (orders start
 * counting), then the end - paid, expired, or the deadline still ahead.
 * Both bonuses are paid at `rewardedAt`, together.
 */
const stepsOf = (referral: RestaurantReferral): TimelineStep[] => {
  const { status, liveAt, qualifyBy, rewardedAt, ordersRequired } = referral;
  const done = lightTheme.darkGray;

  const steps: TimelineStep[] = [
    {
      title: 'Signed up',
      detail: formatReferralDay(referral.signedUpAt),
      done: true,
      color: done,
    },
    {
      title: 'Verified, counting orders',
      detail: liveAt ? formatReferralDay(liveAt) : 'Waiting for verification',
      done: !!liveAt,
      color: done,
    },
  ];

  if (status === 'earned') {
    steps.push({
      title: 'Earned',
      detail: `${formatReferralDay(rewardedAt)} · both bonuses paid`,
      done: true,
      color: lightTheme.green,
    });
  } else if (status === 'expired') {
    steps.push({
      title: 'Expired',
      detail: `${formatReferralDay(
        qualifyBy
      )} · fewer than ${ordersRequired} orders, nobody paid`,
      done: true,
      color: lightTheme.lightGray,
    });
  } else {
    steps.push({
      title: `${ordersRequired} orders by`,
      detail: qualifyBy
        ? `${formatReferralDay(qualifyBy)} · paid within a day of reaching them`
        : 'Set once the restaurant is verified',
      done: false,
      color: lightTheme.darkYellow,
    });
  }

  return steps;
};

export const ReferralTimeline = ({
  referral,
}: {
  referral: RestaurantReferral;
}) => (
  <StepList>
    {stepsOf(referral).map((step) => (
      <Step key={step.title}>
        <StepDot $color={step.color} $done={step.done} />
        <Div $flex $column $gap="2px">
          <H5 $m="0" $color={step.done ? undefined : lightTheme.lightGray}>
            {step.title}
          </H5>
          <Disclaimer $m="0">{step.detail}</Disclaimer>
        </Div>
      </Step>
    ))}
  </StepList>
);
